import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';

export type ImageCropDialogProps = {
  open: boolean;
  imageUrl: string | null;
  onCancel: () => void;
  onSave: (file: File, previewUrl: string) => void;
  outputWidth?: number;
  outputHeight?: number;
  title?: string;
};

// Simple 16:9 cropper with pan and zoom inside a fixed frame
const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  imageUrl,
  onCancel,
  onSave,
  outputWidth = 1920,
  outputHeight = 1080,
  title = 'Resim Kırp'
}) => {
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const frameRef = React.useRef<HTMLDivElement | null>(null);
  const [naturalSize, setNaturalSize] = React.useState<{w:number,h:number}>({ w: 0, h: 0 });
  const [scale, setScale] = React.useState<number>(1);
  const [offset, setOffset] = React.useState<{x:number,y:number}>({ x: 0, y: 0 });
  const draggingRef = React.useRef<{active:boolean, startX:number, startY:number, baseX:number, baseY:number}>({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  const aspectLabel = React.useMemo(() => {
    const g = (n: number) => Math.round(n);
    // Reduce common ratios to 16:9 or 9:16 labels if matches closely
    const r = outputWidth / outputHeight;
    const close = (a: number, b: number) => Math.abs(a - b) < 0.01;
    if (close(r, 16/9)) return '16:9';
    if (close(r, 9/16)) return '9:16';
    return `${g(outputWidth)}:${g(outputHeight)}`;
  }, [outputWidth, outputHeight]);

  React.useEffect(() => {
    if (!open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open]);

  // Ensure the image covers the frame at minimum scale
  const minScale = React.useMemo(() => {
    if (!frameRef.current || !naturalSize.w || !naturalSize.h) return 1;
    const frameW = frameRef.current.clientWidth;
    const frameH = frameRef.current.clientHeight;
    const scaleX = frameW / naturalSize.w;
    const scaleY = frameH / naturalSize.h;
    return Math.max(scaleX, scaleY);
  }, [naturalSize.w, naturalSize.h]);

  React.useEffect(() => {
    if (scale < minScale) setScale(minScale);
  }, [minScale, scale]);

  const startDrag = (clientX: number, clientY: number) => {
    draggingRef.current = { active: true, startX: clientX, startY: clientY, baseX: offset.x, baseY: offset.y };
  };
  const moveDrag = (clientX: number, clientY: number) => {
    if (!draggingRef.current.active) return;
    const dx = clientX - draggingRef.current.startX;
    const dy = clientY - draggingRef.current.startY;
    setOffset({ x: draggingRef.current.baseX + dx, y: draggingRef.current.baseY + dy });
  };
  const endDrag = () => { draggingRef.current.active = false; };

  const handleSave = async () => {
    if (!imageUrl) return;
    // Render the current viewport (16:9) to canvas at output size
    const frame = frameRef.current;
    const img = imgRef.current;
    if (!frame || !img) return;

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Compute how the image maps into the frame
    const frameW = frame.clientWidth;
    const frameH = frame.clientHeight; // should be 16:9 of frameW

    // Current image drawn size inside frame
    const drawW = naturalSize.w * scale;
    const drawH = naturalSize.h * scale;

    // Top-left of the image inside frame (image is centered plus offset)
    const imgX = (frameW - drawW) / 2 + offset.x;
    const imgY = (frameH - drawH) / 2 + offset.y;

    // We want to sample the area of the frame from the transformed image to the output canvas.
    // Compute source rectangle in the original image space corresponding to the frame
    const sx = Math.max(0, (-imgX) / scale);
    const sy = Math.max(0, (-imgY) / scale);
    const sw = Math.min(naturalSize.w - sx, frameW / scale);
    const sh = Math.min(naturalSize.h - sy, frameH / scale);

    // To fill entire canvas, we draw the image portion scaled to output size
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, outputWidth, outputHeight);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outputWidth, outputHeight);

    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
    if (!blob) return;
    const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
    const prevUrl = URL.createObjectURL(blob);
    onSave(file, prevUrl);
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{`${title} (${aspectLabel})`}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Box
              ref={frameRef}
              sx={{
                width: '100%',
                aspectRatio: `${outputWidth} / ${outputHeight}`,
                backgroundColor: '#111',
                borderRadius: 1,
                position: 'relative',
                overflow: 'hidden',
                userSelect: 'none',
                touchAction: 'none',
                cursor: 'grab'
              }}
              onMouseDown={(e) => { startDrag(e.clientX, e.clientY); }}
              onMouseMove={(e) => { moveDrag(e.clientX, e.clientY); }}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
              onTouchStart={(e) => {
                const t = e.touches[0];
                if (t) startDrag(t.clientX, t.clientY);
              }}
              onTouchMove={(e) => {
                const t = e.touches[0];
                if (t) moveDrag(t.clientX, t.clientY);
              }}
              onTouchEnd={endDrag}
            >
              {imageUrl && (
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="crop"
                  onLoad={(e) => {
                    const i = e.currentTarget;
                    setNaturalSize({ w: i.naturalWidth, h: i.naturalHeight });
                    // Reset scale and center
                    setScale(1);
                    setOffset({ x: 0, y: 0 });
                  }}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                    transformOrigin: 'center center',
                    maxWidth: 'none',
                    maxHeight: 'none',
                    willChange: 'transform'
                  }}
                />
              )}
              {/* 16:9 overlay frame border */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.6)'
                }}
              />
            </Box>
            <Box sx={{ mt: 2, px: 1 }}>
              <Slider
                min={minScale}
                max={minScale * 5}
                step={0.01}
                value={scale}
                onChange={(_, v) => setScale(Array.isArray(v) ? v[0] : v)}
              />
            </Box>
          </Box>
          <Box sx={{ width: 260 }}>
            <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>Önizleme ({aspectLabel})</Box>
            <Box sx={{
              width: '100%',
              aspectRatio: `${outputWidth} / ${outputHeight}`,
              backgroundColor: '#000',
              borderRadius: 1,
              position: 'relative',
              overflow: 'hidden',
              userSelect: 'none'
            }}>
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="preview"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                    transformOrigin: 'center center',
                    maxWidth: 'none',
                    maxHeight: 'none'
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Vazgeç</Button>
        <Button variant="contained" onClick={handleSave}>Kaydet</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
